import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { site, integration, customization } from '@yola/ws-sdk';
import displayOptionTypes from '../constants/display-options-type';
import groupDisplayOptions from '../helpers/block-settings/group-display-options';
import BlockSettingsOption from './block-settings-option';
import BlockSettingsOptionBox from './block-settings-option-box';
import BlockSettingsOptionDivider from './block-settings-option-divider';

function BlockSettingsOptions({
  items,
  keySalt,
  withDivider = false,
  updateSharedData = Function.prototype,
}) {
  const { isCleanUrlsSupported } = useSelector(integration.selectors.getSettings);
  const isOnlineStoreCleanUrlSelectorEnabled =
    useSelector(site.selectors.getCreatedWithCleanUrlsDisabled) && Boolean(isCleanUrlsSupported);
  const currentPalette = customization.accessors.getCurrentColorPalette();

  if (!items.length) return null;

  const filteredItems = items.filter(({ type }) => {
    if (!type) return false;
    if (!Object.values(displayOptionTypes).includes(type)) return false;
    if (
      type === displayOptionTypes.ONLINE_STORE_CLEAN_URL_SELECTOR &&
      !isOnlineStoreCleanUrlSelectorEnabled
    )
      return false;

    return true;
  });
  const groupedItems = groupDisplayOptions(filteredItems);

  return (
    <Fragment>
      {groupedItems.map((item, index, allItems) => {
        const isLastItem = index === allItems.length - 1;
        const prevItem = allItems[index - 1];
        const currentItemWithDivider = withDivider && !item.isGrouped && !isLastItem;
        const prevItemWithDivider = withDivider && !!prevItem && !prevItem.isGrouped;

        // SURFACE and CONTAINER_WIDTH items use a stable key to prevent remounting
        // components that hold internal state (ColorTileSelector toggle, custom-value slider)
        const stableKeyTypes = [displayOptionTypes.SURFACE, displayOptionTypes.CONTAINER_WIDTH];
        const itemKey = stableKeyTypes.includes(item.type)
          ? `${item.id}-${item.attribute}`
          : `${item.id}-${item.attribute}-${keySalt}`;

        return (
          <Fragment key={itemKey}>
            <BlockSettingsOptionBox>
              <BlockSettingsOption
                keySalt={keySalt}
                item={item}
                prevItem={prevItem}
                prevItemWithDivider={prevItemWithDivider}
                updateSharedData={updateSharedData}
                currentPalette={currentPalette}
              />
            </BlockSettingsOptionBox>
            {currentItemWithDivider && (
              <BlockSettingsOptionDivider
                {...(item.toggleable && !item.value && { marginTop: 'spacing-s' })}
              />
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

BlockSettingsOptions.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string.isRequired,
      isEnabled: PropTypes.bool,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
      onClick: PropTypes.func,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          onClick: PropTypes.func,
        })
      ),
    })
  ).isRequired,
  keySalt: PropTypes.string.isRequired,
  withDivider: PropTypes.bool,
  updateSharedData: PropTypes.func,
};

export default BlockSettingsOptions;
